import { Environment, UseMutationConfig, useMutation } from "react-relay"
import {
  GraphQLTaggedNode,
  MutationConfig,
  MutationParameters,
  RecordProxy,
  commitMutation,
} from "relay-runtime"

type Variables = {
  [key: string]: any
}
const containsLinkedRecord = (
  record: RecordProxy,
  name: string,
  linkedRecord: RecordProxy,
  args: Variables | null = null,
) => {
  const linkedRecordDataId = linkedRecord.getDataID()
  const linkedRecords = record.getLinkedRecords(name, args) ?? []
  return linkedRecords.some((lr) => lr?.getDataID() === linkedRecordDataId)
}

export const removeLinkedRecord = (
  record: RecordProxy,
  name: string,
  linkedRecord: RecordProxy,
  args: Variables | null = null,
) => {
  if (linkedRecord == null) {
    return
  }

  if (record && containsLinkedRecord(record, name, linkedRecord, args)) {
    const linkedRecords = record.getLinkedRecords(name, args)
    if (linkedRecords) {
      const linkedRecordDataId = linkedRecord.getDataID()
      record.setLinkedRecords(
        linkedRecords.filter((lr) => lr?.getDataID() !== linkedRecordDataId),
        name,
        args,
      )
    }
  }
}

export const addLinkedRecord = (
  record: RecordProxy,
  name: string,
  linkedRecord: RecordProxy,
  args: Variables | null = null,
) => {
  if (linkedRecord == null) {
    return
  }

  if (record && !containsLinkedRecord(record, name, linkedRecord, args)) {
    record.setLinkedRecords(
      [...(record.getLinkedRecords(name, args) || []), linkedRecord],
      name,
      args,
    )
  }
}

export const addLinkedRecords = (
  record: RecordProxy,
  name: string,
  linkedRecords: Array<RecordProxy>,
  args: Variables | null = null,
) => {
  const recordsToAdd = linkedRecords.filter((linkedRecord) => {
    return (
      linkedRecord && !containsLinkedRecord(record, name, linkedRecord, args)
    )
  })

  record.setLinkedRecords(
    [...(record.getLinkedRecords(name, args) || []), ...recordsToAdd],
    name,
    args,
  )
}

/**
 * @deprecated Use `useMutation` instead.
 *
 * Why is `commitMutationPromise` deprecated?
 *
 *
 * The original motivation for `commitMutationPromise` was to improve DX by:
 * 1. Wrapping `commitMutation` in a `Promise` for better flow control.
 * 2. Standardizing error handling and success callbacks.
 * 3. Reducing redundant error reporting.
 * 4. Improving TypeScript type inference.
 *
 * However, `commitMutationPromise` has several drawbacks where `useMutation` is a better alternative:
 *
 * **React’s event handling is synchronous**
 * - Using `async` functions in `onClick` handlers can cause unintended side effects.
 * - React does **not** wait for the async function to resolve—it simply executes it.
 * - This can lead to UI inconsistencies and race conditions.
 *
 * **Promises in event handlers are an anti-pattern**
 * - Promises run **in the background**, meaning state changes triggered within them might not be
 *   immediately reflected in the UI.
 * - If the component re-renders before the promise resolves, React might discard the update.
 *
 * **Memory leaks and state updates on unmounted components**
 * - If a component using `commitMutationPromise` unmounts before the mutation completes,
 *   it may still try to update state, causing memory leaks and warnings.
 *
 *
 * **`useMutation` is a better, idiomatic approach**
 * - Relay's `useMutation` provides a state machine to handle mutations effectively.
 * - It offers:
 *   - **`commit`**: Triggers the mutation.
 *   - **`isInFlight`**: Indicates whether the mutation is in progress.
 * - This ensures state updates synchronize correctly with UI updates.
 *
 *  **It is a worse experience for the user**
 * - Mutations in promises do not provide feedback to the user while a mutation is in progress.
 * - Multiple mutations can be triggered simultaneously
 *
 * **Recommended Alternative: `useMutation`**
 * Instead of using `commitMutationPromise`, use `useMutation` for better control:
 *
 * ```tsx
 * const [commit, isInFlight] = useMutation(SomeMutation);
 * const handleClick = () => {
 *   commit({ variables: { input: { someData: "value" } }});
 * };
 * return <button onClick={handleClick} disabled={isInFlight}>{isInFlight ? "Loading..." : "Submit"}</button>;
 * ```
 *
 * **Prefer a custom hook for reusable mutation logic**
 * If multiple components need the same mutation logic, create a custom hook instead of relying on `commitMutationPromise`:
 *
 * ```tsx
 * function useSomeMutation() {
 *   const [commit, isInFlight] = useMutation(SomeMutation);
 *   function doThing(input: SomeInput) {
 *     commit({
 *       variables: { input },
 *       onSuccess: () => {
 *         toast.success("Success doing thing");
 *       },
 *     });
 *   }
 *   return { commit, isInFlight };
 * }
 * ```
 *
 * This approach is more idiomatic in React and Relay, ensuring predictable UI updates and avoiding unnecessary complexity.
 *
 * **More details:** [Relay Docs: Mutations](https://relay.dev/docs/en/mutations#usemutation)
 *
 */
export const commitMutationPromise = <T extends MutationParameters>(
  environment: Environment,
  config: MutationConfig<T>,
) => {
  return new Promise<T["response"]>((resolve, reject) => {
    commitMutation(environment, {
      ...config,
      onCompleted: (data: T["response"], errors) => {
        if (errors) {
          reject(errors[0])
        } else {
          resolve(data)
        }
      },
      onError: reject,
    })
  })
}

/**
 *
 * @deprecated Use `useMutation` from Relay instead.
 */
export const useMutationPromise = <T extends MutationParameters>(
  mutation: GraphQLTaggedNode,
) => {
  const [mutate, isActive] = useMutation<T>(mutation)
  const beginMutation = (
    config: Omit<UseMutationConfig<T>, "onCompleted" | "onError">,
  ) => {
    return new Promise<T["response"]>((resolve, reject) => {
      mutate({
        ...config,
        onCompleted: (data: T["response"], errors) => {
          if (errors) {
            reject(errors[0])
          } else {
            resolve(data)
          }
        },
        onError: reject,
      })
    })
  }

  return [beginMutation, isActive] as const
}
